/**
 * AnimatedSTEM - компонент с анимацией распада и сборки букв S T E M
 * Каждая буква разбивается на фрагменты с помощью clip-path
 * Анимация запускается раз в 3 секунды, между циклами показывается статичный текст
 * 
 * @param color - цвет текста
 * @param textShadow - тень текста
 */

'use client';

import { useEffect, useMemo, useState } from 'react';

interface AnimatedSTEMProps {
  color: string;
  textShadow: string;
}

// Определения фрагментов для каждой буквы (clip-path полигоны в процентах)
// Каждый фрагмент - это область буквы, которая будет отрываться
const letterFragmentDefs = {
  S: [
    { clip: 'polygon(0% 0%, 100% 0%, 100% 35%, 0% 35%)', origin: '50% 17%' },
    { clip: 'polygon(0% 35%, 40% 35%, 40% 65%, 0% 65%)', origin: '20% 50%' },
    { clip: 'polygon(60% 35%, 100% 35%, 100% 65%, 60% 65%)', origin: '80% 50%' },
    { clip: 'polygon(0% 65%, 100% 65%, 100% 100%, 0% 100%)', origin: '50% 82%' },
  ],
  T: [
    { clip: 'polygon(0% 0%, 35% 0%, 35% 100%, 0% 100%)', origin: '17% 50%' },
    { clip: 'polygon(35% 0%, 65% 0%, 65% 100%, 35% 100%)', origin: '50% 50%' },
    { clip: 'polygon(65% 0%, 100% 0%, 100% 100%, 65% 100%)', origin: '82% 50%' },
  ],
  E: [
    { clip: 'polygon(0% 0%, 100% 0%, 100% 30%, 0% 30%)', origin: '50% 15%' },
    { clip: 'polygon(0% 30%, 70% 30%, 70% 50%, 0% 50%)', origin: '35% 40%' },
    { clip: 'polygon(0% 50%, 70% 50%, 70% 70%, 0% 70%)', origin: '35% 60%' },
    { clip: 'polygon(0% 70%, 100% 70%, 100% 100%, 0% 100%)', origin: '50% 85%' },
  ],
  M: [
    { clip: 'polygon(0% 0%, 25% 0%, 25% 100%, 0% 100%)', origin: '12% 50%' },
    { clip: 'polygon(25% 0%, 50% 0%, 50% 60%, 25% 60%)', origin: '37% 30%' },
    { clip: 'polygon(50% 0%, 75% 0%, 75% 60%, 50% 60%)', origin: '62% 30%' },
    { clip: 'polygon(75% 0%, 100% 0%, 100% 100%, 75% 100%)', origin: '87% 50%' },
  ],
};

export default function AnimatedSTEM({ color, textShadow }: AnimatedSTEMProps) {
  // Состояние для управления циклами анимации
  const [isAnimating, setIsAnimating] = useState(false);

  // Запускаем циклы анимации: 2 сек анимация + 3 сек пауза
  useEffect(() => {
    const ANIMATION_DURATION = 5000; // Длительность анимации в мс
    const PAUSE_DURATION = 3000; // Пауза между циклами в мс

    const startCycle = () => {
      setIsAnimating(true);
      setTimeout(() => {
        setIsAnimating(false);
      }, ANIMATION_DURATION);
    };

    // Первый запуск через паузу
    const initialTimer = setTimeout(startCycle, PAUSE_DURATION);

    // Повторяющиеся циклы
    const interval = setInterval(startCycle, ANIMATION_DURATION + PAUSE_DURATION);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
  }, []);

  // Генерируем параметры анимации для каждого фрагмента
  const fragments = useMemo(() => {
    const letters = ['S', 'T', 'E', 'M'];
    const result: Array<{
      letter: string;
      letterIndex: number;
      fragmentIndex: number;
      clip: string;
      origin: string;
      delay: number;
      spreadX: number;
      spreadY: number;
      spreadZ: number;
      rotX: number;
      rotY: number;
      rotZ: number;
    }> = [];

    letters.forEach((letter, letterIndex) => {
      const defs = letterFragmentDefs[letter as keyof typeof letterFragmentDefs];
      defs.forEach((def, fragmentIndex) => {
        // Случайное направление разлёта от центра фрагмента
        const angle = Math.random() * Math.PI * 2;
        const distance = 60 + Math.random() * 80;
        
        result.push({
          letter,
          letterIndex,
          fragmentIndex,
          clip: def.clip,
          origin: def.origin,
          // Волновой эффект: буквы слева начинают раньше (в секундах для CSS)
          delay: letterIndex * 0.15 + fragmentIndex * 0.1,
          spreadX: Math.cos(angle) * distance,
          spreadY: Math.sin(angle) * distance,
          spreadZ: (Math.random() - 0.5) * 250,
          rotX: (Math.random() - 0.5) * 720,
          rotY: (Math.random() - 0.5) * 720,
          rotZ: (Math.random() - 0.5) * 540,
        });
      });
    });

    return result;
  }, []);

  return (
    <div
      className="relative text-[16px] sm:text-[24px] leading-none"
      style={{
        fontFamily: 'GlitchGoblin, system-ui, sans-serif',
        fontWeight: 400,
        perspective: '1500px',
        transformStyle: 'preserve-3d',
      }}
    >
      <style jsx>{`
        @keyframes fragmentFly {
          0% {
            transform: translate3d(0, 0, 0) rotateX(0deg) rotateY(0deg) rotateZ(0deg);
            opacity: 1;
          }
          50% {
            transform: translate3d(var(--spread-x), var(--spread-y), var(--spread-z)) 
                       rotateX(var(--rot-x)) rotateY(var(--rot-y)) rotateZ(var(--rot-z));
            opacity: 0.4;
          }
          100% {
            transform: translate3d(0, 0, 0) rotateX(0deg) rotateY(0deg) rotateZ(0deg);
            opacity: 1;
          }
        }

        @keyframes colorShift {
          0% {
            color: var(--original-color);
            text-shadow: var(--original-shadow);
          }
          50% {
            color: #8B0000;
            text-shadow: 0 2px 4px rgba(0,0,0,0.7), 0 4px 20px rgba(139,0,0,0.8);
          }
          100% {
            color: var(--original-color);
            text-shadow: var(--original-shadow);
          }
        }

        .fragment {
          will-change: transform, opacity, color, text-shadow;
        }

        .fragment.animating {
          animation: fragmentFly 5s ease-in-out forwards,
                     colorShift 5s ease-in-out forwards;
          animation-delay: var(--delay);
        }

        .static-text {
          opacity: 1;
          transition: opacity 0.3s ease-out;
        }

        .static-text.hidden {
          opacity: 0;
          pointer-events: none;
        }
      `}</style>

      {/* Контейнер букв */}
      <div className="flex gap-[0.5em]">
        {['S', 'T', 'E', 'M'].map((letter, letterIdx) => (
          <div
            key={letterIdx}
            className="relative inline-block"
            style={{
              width: '1em',
              height: '1.2em',
              transformStyle: 'preserve-3d',
            }}
          >
            {/* Статичный текст (показывается между анимациями) */}
            <div
              className={`static-text absolute top-0 left-0 ${isAnimating ? 'hidden' : ''}`}
              style={{
                width: '100%',
                height: '100%',
                color,
                textShadow,
                textRendering: 'geometricPrecision',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {letter}
            </div>

            {/* Фрагменты буквы (показываются только во время анимации) */}
            {isAnimating &&
              fragments
                .filter((f) => f.letter === letter)
                .map((frag, fragIdx) => (
                  <div
                    key={fragIdx}
                    className="fragment animating absolute top-0 left-0 pointer-events-none"
                    style={
                      {
                        width: '100%',
                        height: '100%',
                        color,
                        textShadow,
                        textRendering: 'geometricPrecision',
                        clipPath: frag.clip,
                        transformOrigin: frag.origin,
                        transformStyle: 'preserve-3d',
                        '--original-color': color,
                        '--original-shadow': textShadow,
                        '--delay': `${frag.delay}s`,
                        '--spread-x': `${frag.spreadX}px`,
                        '--spread-y': `${frag.spreadY}px`,
                        '--spread-z': `${frag.spreadZ}px`,
                        '--rot-x': `${frag.rotX}deg`,
                        '--rot-y': `${frag.rotY}deg`,
                        '--rot-z': `${frag.rotZ}deg`,
                      } as React.CSSProperties
                    }
                  >
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {letter}
                    </div>
                  </div>
                ))}
          </div>
        ))}
      </div>
    </div>
  );
}

